import { createContext, createElement, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { catalogs, translate } from './catalog';
import { SUPPORTED_LOCALES, type Locale, type LocaleKey, type LocaleMode, type LocalePreference } from './types';

const STORAGE_KEY = 'flashmate.admin.locale.preference';
const DEFAULT_LOCALE: Locale = 'zh-CN';

export function normalizeLocale(value: string | null | undefined): Locale {
  try {
    const parsed = new Intl.Locale(value?.trim().replace(/_/g, '-') || 'en-US');
    if (parsed.language === 'zh') {
      if (parsed.script === 'Hant' || ['TW', 'HK', 'MO'].includes(parsed.region ?? '')) return 'zh-TW';
      return 'zh-CN';
    }
    if (parsed.language === 'en') return 'en-US';
  } catch { /* invalid language tags use the supported fallback */ }
  return 'en-US';
}

export function readLocalePreference(): LocalePreference {
  if (typeof window === 'undefined') return { localeMode: 'SYSTEM' };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { localeMode: 'SYSTEM' };
    const parsed = JSON.parse(raw) as Partial<LocalePreference>;
    if (parsed?.localeMode === 'USER' && typeof parsed.locale === 'string' && SUPPORTED_LOCALES.includes(parsed.locale as Locale)) {
      return { localeMode: 'USER', locale: parsed.locale as Locale };
    }
  } catch { /* malformed storage falls back to system */ }
  return { localeMode: 'SYSTEM' };
}

export function persistLocalePreference(preference: LocalePreference): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
  } catch { /* the selected language remains usable when browser storage is unavailable */ }
}

function detectSystemLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  return normalizeLocale(navigator.language);
}

type I18nContextValue = Readonly<{
  locale: Locale;
  localeMode: LocaleMode;
  setLocale: (locale: Locale) => void;
  useSystemLocale: () => void;
  t: (key: LocaleKey, values?: Readonly<Record<string, string | number>>) => string;
}>;

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: PropsWithChildren) {
  const [preference, setPreference] = useState<LocalePreference>(() => readLocalePreference());
  const [systemLocale, setSystemLocale] = useState<Locale>(() => detectSystemLocale());
  const locale = preference.localeMode === 'USER' && preference.locale ? preference.locale : systemLocale;
  useEffect(() => {
    const handleLanguageChange = () => setSystemLocale(detectSystemLocale());
    window.addEventListener('languagechange', handleLanguageChange);
    return () => window.removeEventListener('languagechange', handleLanguageChange);
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const value = useMemo<I18nContextValue>(() => {
    const persist = (next: LocalePreference) => {
      setPreference(next);
      persistLocalePreference(next);
    };
    return {
      locale,
      localeMode: preference.localeMode,
      setLocale: (next) => persist({ localeMode: 'USER', locale: next }),
      useSystemLocale: () => persist({ localeMode: 'SYSTEM' }),
      t: (key, values) => translate(locale, key, values),
    };
  }, [locale, preference.localeMode]);
  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
}

export { catalogs, clearMissingTranslationEvents, getMissingTranslationEvents } from './catalog';
export { STORAGE_KEY };
export type { Locale, LocaleKey, LocaleMode, LocalePreference } from './types';
